import { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND } from "@/lib/api";
import { deepMerge } from "@/lib/pageContent";
import ImageUploader from "@/components/ImageUploader";
import RichTextField from "@/components/RichTextField";
import { PAGE_IMAGE_SLOTS, slotSettingsKey } from "@/content/pageImages";
import { EVENEMENTS_CONTENT } from "@/content/evenements";
import { MARIAGE_CONTENT } from "@/content/mariage";
import { SEMINAIRE_CONTENT } from "@/content/seminaire";
import { BAPTEME_CONTENT } from "@/content/bapteme";
import { ANNIVERSAIRE_CONTENT } from "@/content/anniversaire";
import { MARIAGE_HALAL_CONTENT } from "@/content/mariageHalal";
import { MARIAGE_JUIF_CONTENT } from "@/content/mariageJuif";
import { TRANSLATIONS } from "@/i18n/translations";
import { LABELS } from "@/pages/admin/contentLabels";
import {
  Save, RotateCcw, Plus, Minus, ExternalLink, Monitor, Smartphone,
  RefreshCw, Image as ImageIcon, ChevronRight, Eye, EyeOff,
} from "lucide-react";

const SITE_TEXTS = (section) => ({ fr: TRANSLATIONS.fr[section], en: TRANSLATIONS.en[section] });

const PAGES = [
  { key: "accueil", label: "Accueil", path: "/", content: SITE_TEXTS("home") },
  { key: "carte", label: "La carte", path: "/menu", content: SITE_TEXTS("menu") },
  { key: "contact", label: "Contact", path: "/contact", content: SITE_TEXTS("contact") },
  { key: "evenements", label: "Événements (hub)", path: "/evenements", content: EVENEMENTS_CONTENT },
  { key: "mariage", label: "Mariage", path: "/mariage", content: MARIAGE_CONTENT },
  { key: "seminaire", label: "Séminaire", path: "/seminaire", content: SEMINAIRE_CONTENT },
  { key: "bapteme", label: "Baptême & communion", path: "/bapteme", content: BAPTEME_CONTENT },
  { key: "anniversaire", label: "Anniversaire", path: "/anniversaire", content: ANNIVERSAIRE_CONTENT },
  { key: "mariage-halal", label: "Mariage halal", path: "/mariage-halal-ville-davray", content: MARIAGE_HALAL_CONTENT },
  { key: "mariage-juif", label: "Mariage juif", path: "/mariage-juif-ville-davray", content: MARIAGE_JUIF_CONTENT },
];

const lab = (k) => LABELS[k] || k;
const HERO_KEYS = ["overline", "h1", "intro", "ctaDevis", "ctaCall", "heroAlt", "breadcrumb"];
const resolveUrl = (u) => (u?.startsWith("/api/") ? `${BACKEND}${u}` : u);

/* ---------- helpers on the content tree ---------- */

function flattenLeaves(node, prefix = []) {
  if (typeof node === "string" || typeof node === "number") {
    return [{ path: prefix, value: String(node) }];
  }
  if (Array.isArray(node)) return node.flatMap((v, i) => flattenLeaves(v, [...prefix, String(i)]));
  if (node && typeof node === "object") {
    return Object.entries(node).flatMap(([k, v]) => flattenLeaves(v, [...prefix, k]));
  }
  return [];
}

function collectArrays(node, prefix = []) {
  if (Array.isArray(node)) {
    return [
      { path: prefix, length: node.length },
      ...node.flatMap((v, i) => collectArrays(v, [...prefix, String(i)])),
    ];
  }
  if (node && typeof node === "object") {
    return Object.entries(node).flatMap(([k, v]) => collectArrays(v, [...prefix, k]));
  }
  return [];
}

const getAt = (obj, path) => path.reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

function setAt(obj, path, value) {
  if (path.length === 0) return value;
  const [k, ...rest] = path;
  if (Array.isArray(obj)) {
    const next = obj.slice();
    next[Number(k)] = setAt(obj[Number(k)], rest, value);
    return next;
  }
  return { ...obj, [k]: setAt(obj?.[k], rest, value) };
}

const blankLike = (v) =>
  Array.isArray(v)
    ? v.map(blankLike)
    : v && typeof v === "object"
    ? Object.fromEntries(Object.keys(v).map((k) => [k, blankLike(v[k])]))
    : "";

function deepDiff(base, draft) {
  if (Array.isArray(base) || Array.isArray(draft)) {
    return JSON.stringify(base) === JSON.stringify(draft) ? undefined : draft;
  }
  if (base && draft && typeof base === "object" && typeof draft === "object") {
    const out = {};
    Object.keys(draft).forEach((k) => {
      const d = deepDiff(base[k], draft[k]);
      if (d !== undefined) out[k] = d;
    });
    return Object.keys(out).length ? out : undefined;
  }
  return base === draft ? undefined : draft;
}

const CELL_LABELS = { 2: ["Intitulé", "Valeur"], 3: ["Intitulé", "Colonne 2", "Colonne 3"] };

const fieldLabel = (path, root) =>
  path
    .map((seg, i) => {
      if (!/^\d+$/.test(seg)) return lab(seg);
      const parent = getAt(root, path.slice(0, i));
      const cells = CELL_LABELS[parent?.length];
      if (Array.isArray(parent) && cells && typeof parent[0] === "string") {
        return cells[Number(seg)] || `#${Number(seg) + 1}`;
      }
      return `#${Number(seg) + 1}`;
    })
    .slice(1)
    .join(" › ") || lab(path[0]);

/** Sections of a page: a "hero" group plus one section per remaining key. */
function buildSections(content) {
  if (!content) return [];
  const keys = Object.keys(content);
  const heroKeys = keys.filter((k) => HERO_KEYS.includes(k));
  const sections = [];
  if (heroKeys.length > 1) sections.push({ id: "hero", title: "Bandeau principal", keys: heroKeys });
  keys
    .filter((k) => !heroKeys.includes(k) || heroKeys.length <= 1)
    .forEach((k) => sections.push({ id: k, title: lab(k), keys: [k] }));
  return sections;
}

/* ---------- component ---------- */

export default function PageEditor() {
  const [pageKey, setPageKey] = useState("evenements");
  const [lang, setLang] = useState("fr");
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(null);
  const [settings, setSettings] = useState({});
  const [savedSettings, setSavedSettings] = useState({});
  const [sectionId, setSectionId] = useState("hero");
  const [device, setDevice] = useState("desktop");
  const [showPreview, setShowPreview] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);
  const [status, setStatus] = useState({ loading: true, saving: false, msg: "" });
  const scrollRef = useRef(null);

  const page = PAGES.find((p) => p.key === pageKey);
  const defaults = useMemo(() => page.content[lang], [page, lang]);
  const sections = useMemo(() => buildSections(draft), [draft]);
  const slots = PAGE_IMAGE_SLOTS[pageKey] || [];

  useEffect(() => {
    setStatus({ loading: true, saving: false, msg: "" });
    Promise.all([
      api.get(`/page-content/${pageKey}?lang=${lang}`).catch(() => ({ data: {} })),
      api.get("/settings").catch(() => ({ data: {} })),
    ])
      .then(([content, sett]) => {
        const merged = deepMerge(defaults, content.data?.data || {});
        setDraft(merged);
        setSaved(merged);
        setSettings(sett.data || {});
        setSavedSettings(sett.data || {});
        setSectionId((prev) => {
          const ids = buildSections(merged).map((s) => s.id);
          return ids.includes(prev) ? prev : ids[0];
        });
      })
      .finally(() => setStatus({ loading: false, saving: false, msg: "" }));
  }, [pageKey, lang, defaults]);

  const dirty =
    (draft && saved && JSON.stringify(draft) !== JSON.stringify(saved)) ||
    JSON.stringify(settings) !== JSON.stringify(savedSettings);

  const section = sections.find((s) => s.id === sectionId) || sections[0];
  const sectionSlots = slots.filter((s) => s.section === section?.id);

  const update = (path, value) => setDraft((d) => setAt(d, path, value));

  const leaves = useMemo(() => {
    if (!draft || !section) return [];
    return section.keys.flatMap((k) => flattenLeaves(draft[k], [k]));
  }, [draft, section]);

  const arrays = useMemo(() => {
    if (!draft || !section) return [];
    return section.keys
      .flatMap((k) => collectArrays(draft[k], [k]))
      .filter((a) => !a.path.slice(1).some((seg) => /^\d+$/.test(seg)));
  }, [draft, section]);

  const save = async () => {
    setStatus((s) => ({ ...s, saving: true, msg: "" }));
    try {
      await api.put(`/admin/page-content/${pageKey}?lang=${lang}`, {
        data: deepDiff(defaults, draft) || {},
      });
      const changedKeys = Object.keys(settings).filter((k) => settings[k] !== savedSettings[k]);
      for (const k of changedKeys) {
        await api.put(`/settings/${encodeURIComponent(k)}`, { value: settings[k] || "" });
      }
      setSaved(draft);
      setSavedSettings(settings);
      setPreviewKey((k) => k + 1);
      setStatus({ loading: false, saving: false, msg: "Modifications publiées sur le site." });
    } catch (e) {
      setStatus({ loading: false, saving: false, msg: "Erreur lors de l'enregistrement." });
    }
  };

  const reset = async () => {
    if (!window.confirm("Rétablir le texte d'origine de cette page ?")) return;
    await api.delete(`/admin/page-content/${pageKey}?lang=${lang}`);
    setDraft(defaults);
    setSaved(defaults);
    setPreviewKey((k) => k + 1);
    setStatus({ loading: false, saving: false, msg: "Texte d'origine rétabli." });
  };

  const previewUrl = `${lang === "en" ? `/en${page.path === "/" ? "" : page.path}` : page.path}?preview=1`;

  return (
    <div data-testid="admin-page-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <p className="overline">Pages du site</p>
          <h1 className="font-display text-3xl md:text-4xl mt-2">Éditeur de pages</h1>
        </div>
        <div className="flex flex-wrap items-end gap-3 ml-auto">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[#697585]">Page</span>
            <select
              data-testid="editor-page-select"
              value={pageKey}
              onChange={(e) => setPageKey(e.target.value)}
              className="mt-1 block border border-[#D9D3C5] bg-white px-3 py-2 text-sm"
            >
              {PAGES.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.16em] text-[#697585]">Langue</span>
            <select
              data-testid="editor-lang-select"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              className="mt-1 block border border-[#D9D3C5] bg-white px-3 py-2 text-sm"
            >
              <option value="fr">Français</option>
              <option value="en">Anglais</option>
            </select>
          </label>
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="editor-open-page"
            className="inline-flex items-center gap-2 text-sm text-[#B8956A] hover:text-[#203240] pb-2"
          >
            Ouvrir la page <ExternalLink size={13} />
          </a>
        </div>
      </div>

      <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 md:-mx-10 px-4 sm:px-6 md:px-10 py-3 mt-5 bg-[#F6F0E7]/95 backdrop-blur border-y border-[#D9D3C5] flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={status.saving || status.loading} data-testid="editor-save" className="btn-primary">
          <Save size={14} /> {status.saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button
          onClick={reset}
          data-testid="editor-reset"
          className="inline-flex items-center gap-2 border border-[#D9D3C5] px-4 py-2 text-sm text-[#697585] hover:border-[#B8956A] hover:text-[#B8956A]"
        >
          <RotateCcw size={13} /> Texte d'origine
        </button>
        {dirty && (
          <span data-testid="editor-dirty" className="text-xs uppercase tracking-[0.14em] text-[#B8956A] border border-[#B8956A] px-2 py-1">
            Modifications non enregistrées
          </span>
        )}
        {status.msg && <span data-testid="editor-status" className="text-sm text-[#5A7A5A]">{status.msg}</span>}

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowPreview((v) => !v)}
            data-testid="editor-toggle-preview"
            className="inline-flex items-center gap-1.5 border border-[#D9D3C5] px-3 py-2 text-xs text-[#697585] hover:border-[#B8956A]"
          >
            {showPreview ? <EyeOff size={13} /> : <Eye size={13} />} Aperçu
          </button>
          {showPreview && (
            <>
              <button
                onClick={() => setDevice("desktop")}
                data-testid="editor-device-desktop"
                className={`p-2 border ${device === "desktop" ? "border-[#B8956A] text-[#B8956A]" : "border-[#D9D3C5] text-[#697585]"}`}
              >
                <Monitor size={14} />
              </button>
              <button
                onClick={() => setDevice("mobile")}
                data-testid="editor-device-mobile"
                className={`p-2 border ${device === "mobile" ? "border-[#B8956A] text-[#B8956A]" : "border-[#D9D3C5] text-[#697585]"}`}
              >
                <Smartphone size={14} />
              </button>
              <button
                onClick={() => setPreviewKey((k) => k + 1)}
                data-testid="editor-refresh-preview"
                className="p-2 border border-[#D9D3C5] text-[#697585] hover:border-[#B8956A]"
              >
                <RefreshCw size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {status.loading || !draft ? (
        <p className="mt-8 text-sm text-[#697585]">Chargement…</p>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sections */}
          <nav className="lg:col-span-3" data-testid="editor-sections">
            <p className="overline mb-3">Sections</p>
            <ul className="border border-[#D9D3C5] bg-white divide-y divide-[#EFE7DA] max-h-[70vh] overflow-y-auto">
              {sections.map((s) => {
                const count = s.keys.flatMap((k) => flattenLeaves(draft[k], [k])).length;
                const imgs = slots.filter((sl) => sl.section === s.id).length;
                const active = s.id === section?.id;
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => {
                        setSectionId(s.id);
                        scrollRef.current?.scrollTo({ top: 0 });
                      }}
                      data-testid={`editor-section-${s.id}`}
                      className={`w-full text-left px-4 py-3 flex items-center gap-2 text-sm transition-colors ${
                        active ? "bg-[#F6F0E7] text-[#B8956A]" : "text-[#203240] hover:bg-[#FBF7F1]"
                      }`}
                    >
                      <span className="flex-1 truncate">{s.title}</span>
                      {imgs > 0 && <ImageIcon size={12} className="text-[#B8956A]" />}
                      <span className="text-[10px] text-[#697585]">{count}</span>
                      <ChevronRight size={12} className={active ? "text-[#B8956A]" : "text-[#D9D3C5]"} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Fields */}
          <div className={showPreview ? "lg:col-span-4" : "lg:col-span-9"}>
            <p className="overline mb-3">{section?.title}</p>
            <div
              ref={scrollRef}
              className="border border-[#D9D3C5] bg-white p-5 space-y-5 max-h-[70vh] overflow-y-auto"
            >
              {sectionSlots.map((slot) => {
                const key = slotSettingsKey(pageKey, slot);
                const current = settings[key] || settings[slot.fallback];
                return (
                  <div key={key} className="border border-[#EFE7DA] bg-[#FBF7F1] p-4" data-testid={`editor-img-${slot.slot}`}>
                    <p className="text-[11px] uppercase tracking-[0.16em] text-[#B8956A]">{slot.label}</p>
                    <p className="text-xs text-[#697585] mt-1">{slot.description}</p>
                    {current && (
                      <div className="mt-3 aspect-[3/2] overflow-hidden border border-[#D9D3C5]">
                        <img src={resolveUrl(current)} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="mt-3">
                      <ImageUploader
                        label="Changer la photo"
                        value={settings[key]}
                        onChange={(url) => setSettings((v) => ({ ...v, [key]: url }))}
                      />
                    </div>
                    {settings[key] && (
                      <button
                        type="button"
                        onClick={() => setSettings((v) => ({ ...v, [key]: "" }))}
                        data-testid={`editor-img-clear-${slot.slot}`}
                        className="mt-3 text-xs text-[#697585] underline hover:text-[#B8956A]"
                      >
                        Rétablir la photo d'origine
                      </button>
                    )}
                  </div>
                );
              })}

              {leaves.map((leaf) => {
                const id = leaf.path.join(".");
                const rich = leaf.value.length > 120;
                return (
                  <label key={id} className="block">
                    <span className="text-[11px] uppercase tracking-[0.16em] text-[#697585]">
                      {fieldLabel(leaf.path, draft)}
                    </span>
                    {rich ? (
                      <RichTextField
                        value={leaf.value}
                        onChange={(v) => update(leaf.path, v)}
                        testid={`editor-field-${id}`}
                      />
                    ) : (
                      <input
                        data-testid={`editor-field-${id}`}
                        value={leaf.value}
                        onChange={(e) => update(leaf.path, e.target.value)}
                        className="mt-1 w-full border border-[#D9D3C5] bg-white px-3 py-2 text-sm text-[#203240] focus:border-[#B8956A] outline-none"
                      />
                    )}
                  </label>
                );
              })}

              {arrays.length > 0 && (
                <div className="pt-4 border-t border-[#EFE7DA] space-y-2">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[#B8956A]">
                    Ajouter / retirer des éléments
                  </p>
                  {arrays.map((a) => {
                    const id = a.path.join(".");
                    return (
                      <div key={id} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-[#697585] truncate">
                          {fieldLabel(a.path, draft) || section.title} · {a.length}
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            data-testid={`editor-add-${id}`}
                            onClick={() => {
                              const arr = getAt(draft, a.path) || [];
                              update(a.path, [...arr, arr.length ? blankLike(arr[arr.length - 1]) : ""]);
                            }}
                            className="inline-flex items-center gap-1 border border-[#D9D3C5] px-2 py-1 text-xs text-[#697585] hover:border-[#B8956A] hover:text-[#B8956A]"
                          >
                            <Plus size={11} /> Ajouter
                          </button>
                          <button
                            type="button"
                            data-testid={`editor-remove-${id}`}
                            onClick={() => {
                              const arr = getAt(draft, a.path) || [];
                              if (arr.length > 1) update(a.path, arr.slice(0, -1));
                            }}
                            className="inline-flex items-center gap-1 border border-[#D9D3C5] px-2 py-1 text-xs text-[#697585] hover:border-red-400 hover:text-red-500"
                          >
                            <Minus size={11} /> Retirer
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Live preview */}
          {showPreview && (
            <div className="lg:col-span-5">
              <p className="overline mb-3">Aperçu {device === "mobile" ? "mobile" : "ordinateur"}</p>
              <div className="border border-[#D9D3C5] bg-[#203240] p-3 flex justify-center">
                <iframe
                  key={previewKey}
                  src={previewUrl}
                  title="Aperçu de la page"
                  data-testid="editor-preview"
                  className="bg-white"
                  style={{
                    width: device === "mobile" ? 390 : "100%",
                    height: "70vh",
                    border: 0,
                  }}
                />
              </div>
              <p className="text-xs text-[#697585] mt-2">
                L'aperçu se met à jour après chaque enregistrement.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
